import i18next from 'i18next';
import PropTypes from 'prop-types';
import React from 'react';

import DisplayNumber, { formatNumber, PRICE_NUMBER_DECIMALS } from '@commons/DisplayNumber';
import EmptyState from '@commons/EmptyState';
import Text, { ENUM_COLORS, ENUM_FONTS } from '@commons/Text';

import {
  Cell,
  ColumnsRow,
  MissingReferenceDot,
  ProductRow,
  TableContainer,
} from '../../../orderTableStyledComponents';
import OrderCategoryRow from '../../../OrderCategoryRow';
import OrderProductNameCell from '../../../OrderProductNameCell';

import PreparedQuantityCell from './components/PreparedQuantityCell';

const COLUMN_FLEX = {
  PRODUCT: 4,
  BDC_QUANTITY: 2,
  BDL_QUANTITY: 2,
  PRICE: 2,
  BDL_TOTAL: 2,
};

/**
 * Lines of an order from `En préparation` onwards, grouped by category like the draft table: the
 * ordered quantities (BDC) next to the delivered ones (BDL), the line total following the latter.
 * While the order is in preparation the BDL is typed in — gated by the edit right — and once it is
 * prepared the same column freezes into plain text, a delivery unfaithful to the order standing out
 * in bold.
 */
const OrderPreparationProductTable = (props) => {
  const {
    categoryGroups,
    collapsedCategories,
    hasSearch,
    isPrepared,
    isEditAllowed,
    onToggleCategory,
    onQuantityChange,
    onToggleChecked,
  } = props;

  if (!categoryGroups.length) {
    return (
      <EmptyState
        label={i18next.t(
          hasSearch
            ? 'EXTERNAL_ORDERS.FORM.NO_PRODUCT_SEARCH_RESULT'
            : 'EXTERNAL_ORDERS.FORM.NO_ORDER_PRODUCT',
        )}
      />
    );
  }

  const renderColumnLabel = (label) => (
    <Text color={ENUM_COLORS.DARKER} font={ENUM_FONTS.TEXT_BIG_HEIGHT_16}>
      {label}
    </Text>
  );

  /**
   * The prepared view spells a delivery unfaithful to the order out in bold, on the BDL quantity
   * and on the line total alike — while the preparation lasts, the cell tint already carries it.
   */
  const getBdlFont = ({ quantity, preparedQuantity }) => {
    const isDeliveredAsOrdered = (preparedQuantity ?? 0) === quantity;

    if (!isPrepared || isDeliveredAsOrdered) {
      return ENUM_FONTS.TEXT_BIG_HEIGHT_16;
    }

    return ENUM_FONTS.TEXT_MIDDLE_BOLD;
  };

  const renderPreparedQuantity = (productLine) => {
    const { productId, quantity, preparedQuantity, checked } = productLine;

    if (!isPrepared) {
      return (
        <PreparedQuantityCell
          bdcQuantity={quantity}
          isChecked={checked}
          isDisabled={!isEditAllowed}
          quantity={preparedQuantity}
          onQuantityChange={(value) => onQuantityChange(productId, value)}
          onToggleChecked={() => onToggleChecked(productId)}
        />
      );
    }

    return (
      <Text color={ENUM_COLORS.DARKEST} font={getBdlFont(productLine)}>
        {formatNumber(preparedQuantity ?? 0)}
      </Text>
    );
  };

  /**
   * An undelivered line — a BDL quantity down to 0 — is flagged by a dot at the start of the row.
   * While the preparation lasts, the flag follows the tick exactly like the orange cell tint does;
   * on a prepared order the frozen quantity alone carries it.
   */
  const isReferenceMissing = ({ preparedQuantity, checked }) => {
    if ((preparedQuantity ?? 0) !== 0) {
      return false;
    }

    return isPrepared || checked;
  };

  const renderProductRow = (productLine) => {
    const { id, name, sku, img, priceWithTaxes, quantity, preparedQuantity } = productLine;

    return (
      <ProductRow key={id}>
        {isReferenceMissing(productLine) && <MissingReferenceDot />}
        <Cell flex={COLUMN_FLEX.PRODUCT}>
          <OrderProductNameCell img={img} name={name} sku={sku} />
        </Cell>
        <Cell flex={COLUMN_FLEX.BDC_QUANTITY}>
          <Text color={ENUM_COLORS.DARKEST} font={ENUM_FONTS.TEXT_BIG_HEIGHT_16}>
            {formatNumber(quantity)}
          </Text>
        </Cell>
        <Cell flex={COLUMN_FLEX.BDL_QUANTITY}>{renderPreparedQuantity(productLine)}</Cell>
        <Cell flex={COLUMN_FLEX.PRICE}>
          <DisplayNumber
            customNumberDecimal={PRICE_NUMBER_DECIMALS}
            font={ENUM_FONTS.TEXT_BIG_HEIGHT_16}
            number={priceWithTaxes}
            displayCurrencyCode
          />
        </Cell>
        <Cell flex={COLUMN_FLEX.BDL_TOTAL}>
          <DisplayNumber
            font={getBdlFont(productLine)}
            number={(preparedQuantity ?? 0) * priceWithTaxes}
            displayCurrencyCode
          />
        </Cell>
      </ProductRow>
    );
  };

  return (
    <TableContainer>
      <ColumnsRow>
        <Cell flex={COLUMN_FLEX.PRODUCT}>{renderColumnLabel(i18next.t('GENERAL.PRODUCT'))}</Cell>
        <Cell flex={COLUMN_FLEX.BDC_QUANTITY}>
          {renderColumnLabel(i18next.t('EXTERNAL_ORDERS.FORM.COLUMN_QUANTITY_BDC'))}
        </Cell>
        <Cell flex={COLUMN_FLEX.BDL_QUANTITY}>
          {renderColumnLabel(i18next.t('EXTERNAL_ORDERS.FORM.COLUMN_QUANTITY_BDL'))}
        </Cell>
        <Cell flex={COLUMN_FLEX.PRICE}>
          {renderColumnLabel(i18next.t('EXTERNAL_ORDERS.FORM.COLUMN_UNIT_PRICE'))}
        </Cell>
        <Cell flex={COLUMN_FLEX.BDL_TOTAL}>
          {renderColumnLabel(i18next.t('EXTERNAL_ORDERS.FORM.COLUMN_TOTAL_BDL'))}
        </Cell>
      </ColumnsRow>

      {categoryGroups.map(({ category, products }) => {
        const isCollapsed = collapsedCategories.includes(category);

        return (
          <div key={category}>
            <OrderCategoryRow
              category={category}
              isCollapsed={isCollapsed}
              productCount={products.length}
              onToggle={() => onToggleCategory(category)}
            />
            {!isCollapsed && products.map(renderProductRow)}
          </div>
        );
      })}
    </TableContainer>
  );
};

OrderPreparationProductTable.propTypes = {
  categoryGroups: PropTypes.arrayOf(
    PropTypes.shape({
      category: PropTypes.string,
      products: PropTypes.array,
    }),
  ).isRequired,
  collapsedCategories: PropTypes.arrayOf(PropTypes.string).isRequired,
  hasSearch: PropTypes.bool.isRequired,
  isPrepared: PropTypes.bool.isRequired,
  isEditAllowed: PropTypes.bool,
  onToggleCategory: PropTypes.func.isRequired,
  onQuantityChange: PropTypes.func,
  onToggleChecked: PropTypes.func,
};

OrderPreparationProductTable.defaultProps = {
  isEditAllowed: false,
  onQuantityChange: () => {},
  onToggleChecked: () => {},
};

export default OrderPreparationProductTable;
